'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { AuditProgressScreen } from './AuditProgressScreen';

interface AuditLoadingStateProps {
  /** Report tier, used to pick realistic step timings. */
  tier?: 'free' | 'pro' | 'expert';
  /** URL being analyzed, shown in the progress header. */
  url?: string;
}

/**
 * Processing state for a report whose analysis is still running in the
 * background (the Pro audit responds immediately and finishes out of band).
 * Renders the full progress experience (progress ring, time estimate, step
 * timeline) so the wait shows real progress instead of a generic spinner, and
 * polls the server component so the page flips to the results / failed /
 * manual_review view as soon as the background work updates the report row.
 */
export function AuditLoadingState({ tier, url }: AuditLoadingStateProps) {
  const router = useRouter();

  // This page is a server component, so poll for the updated status: refresh
  // re-fetches the report, and once it flips to completed / failed /
  // manual_review the page renders that state instead and this unmounts.
  useEffect(() => {
    const interval = setInterval(() => {
      router.refresh();
    }, 5000);
    return () => clearInterval(interval);
  }, [router]);

  return <AuditProgressScreen tier={tier === 'free' ? 'free' : 'paid'} url={url} />;
}
